import { useEffect, useMemo, useRef, useState } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import Sidebar, { type Badges } from './Sidebar'
import BotonAvisos from './BotonAvisos'
import SubTabs from './SubTabs'
import AvailabilityPill from './AvailabilityPill'
import apiClient from '../api/client'
import { useAuth } from '../auth/useAuth'
import { resolveScreen } from '../nav'
import { BANDEJA_CAMBIO, EXPEDIENTE_LISTO, MENSAJE_NUEVO } from '../eventos'
import { SidebarColapsado } from '../sidebarEstado'
import { sidebarColapsado, ventanaAngosta } from '../disposicion'
import {
  armarAviso, avisoDeExpedienteListo, entrantesNuevos, queAbrir, type Entrante,
} from '../avisos'
import { avisar, iconoDelAviso, portalALaVista, relojDeFondo, sonar } from '../campana'
import { AvisosCtx, useAvisos } from '../useAvisos'

interface PendingCount {
  /** Cuántos clientes MÍOS esperan respuesta: es el número de la campana. */
  my_waiting?: number
  /**
   * Las cuatro marcas del sonido. Son monótonas: el navegador las compara con
   * las del sondeo anterior y, si alguna avanzó, suena. Un contador no alcanza
   * —si un cliente escribe mientras otro queda atendido dentro del mismo
   * intervalo, el número no se mueve y el mensaje nuevo pasa sin ruido—.
   *
   * Cuáles se escuchan lo decide el ROL, abajo.
   */
  my_last_inbound_ts?: string
  my_last_assigned_ts?: string
  any_last_inbound_ts?: string
  queue_last_ts?: string
  /** El último mensaje del cliente en CUALQUIERA de las mías, en cualquier
   *  estado: no suena, refresca la lista para que aparezca su número. */
  my_any_inbound_ts?: string
  /** Quién escribió, para el pop-up: lo de supervisión y lo del asesor, con
   *  el mismo recorte que la marca del sonido de cada rol. */
  entrantes?: Entrante[]
  mis_entrantes?: Entrante[]
  pending_unassigned: number
  my_assigned: number
  docs_pending_review: number
  /** Mis expedientes completos que nadie descargó (03-nov-2026). */
  my_por_descargar?: number
  /** La hora del último expediente mío que quedó listo: avanza y suena. */
  my_last_completo_ts?: string
}

const SIN_PENDIENTES: Badges = { inbox: 0, queue: 0, documents: 0, expedientes: 0 }

/**
 * Shell del portal: sidebar por rol + header de 70 px + barra de sub-pestañas.
 *
 * El título de la pantalla sale de `src/nav.ts`, no de un mapa local: antes
 * vivía acá y quedaba desincronizado cada vez que alguien agregaba una página
 * (el header decía "Portal PGH" en media docena de rutas).
 */
export default function Layout() {
  // EL SIDEBAR SE COLAPSA SOLO EN UNA VENTANA ANGOSTA (DEF-031, 07-oct-2026)
  //
  // Expandido mide 236 px y debajo de 940 la fila de la bandeja o de En vivo
  // no entra con él: el portal entra desde 768 (`disposicion.ts`). Quien lo
  // toca manda mientras la ventana siga angosta (`enAngosto`); al ensancharla
  // vuelve lo que había elegido con la ventana ancha (`elegido`, expandido de
  // entrada) y el próximo angostamiento empieza colapsado otra vez. Se guarda
  // si la ventana ES angosta y no su ancho: cada píxel de un resize no
  // repinta el portal entero.
  const [elegido, setElegido] = useState(false)
  const [enAngosto, setEnAngosto] = useState<boolean | null>(null)
  const [angosta, setAngosta] = useState(() => ventanaAngosta(window.innerWidth))
  useEffect(() => {
    const medir = () => setAngosta(ventanaAngosta(window.innerWidth))
    window.addEventListener('resize', medir)
    return () => window.removeEventListener('resize', medir)
  }, [])
  useEffect(() => { if (!angosta) setEnAngosto(null) }, [angosta])
  const sidebarCollapsed = sidebarColapsado(angosta, elegido, enAngosto)
  const alternarSidebar = () => {
    if (angosta) setEnAngosto(!sidebarCollapsed)
    else setElegido(!sidebarCollapsed)
  }
  const { role, atiende, user } = useAuth()
  const { pathname } = useLocation()
  const navigate = useNavigate()

  // Contadores para los badges. Un solo endpoint alimenta los tres: bandeja
  // propia, cola global y documentos por revisar.
  const [badges, setBadges] = useState<Badges>(SIN_PENDIENTES)
  //: Cuántos clientes míos esperan que conteste. Es el número de la campana.
  const [esperando, setEsperando] = useState(0)
  // Sonido y pop-up: las preferencias, el panel de «Avisos» y sus pruebas.
  const avisos = useAvisos()

  //: El entrante más reciente del sondeo ANTERIOR. Vive en un ref y no en el
  //: estado: cambiarlo no tiene que repintar nada, y si fuera estado el
  //: `useEffect` se volvería a montar en cada sondeo.
  const ultimoEntrante = useRef<string | null>(null)
  //: Lo asignado a mí en el sondeo anterior: cuántas y la última asignación.
  //: Cuando cambia, la bandeja se refresca sin esperar a su propio sondeo.
  const ultimaBandeja = useRef<string | null>(null)
  //: Quién escribió en el sondeo anterior, para el pop-up. `null` hasta el
  //: primero: al abrir el portal no se avisa de lo que ya estaba.
  const entrantesPrevios = useRef<Entrante[] | null>(null)
  //: El último mensaje de un cliente en lo de quien mira, para refrescar la
  //: lista cuando cambia (`MENSAJE_NUEVO`).
  const ultimoDeLaLista = useRef<string | null>(null)
  //: La hora del último expediente mío que quedó listo, del sondeo anterior:
  //: cuando avanza, «Mis expedientes» se refresca y sale el pop-up.
  const ultimoListo = useRef<string | null>(null)
  //: Las preferencias y quién mira se leen dentro del sondeo, así que van en
  //: refs: cambiarlas no puede rearmar el sondeo y perder la marca anterior.
  const avisosRef = useRef(avisos.estado)
  avisosRef.current = avisos.estado
  const miEmailRef = useRef('')
  miEmailRef.current = (user?.email ?? '').toLowerCase()
  const navigateRef = useRef(navigate)
  navigateRef.current = navigate

  //: Quién oye TODO. Mismo motivo de ref que el anterior: cambiar de rol no
  //: puede reiniciar el sondeo y perder la marca contra la que se compara.
  const supervisa = role === 'supervisor' || role === 'admin'
  const supervisaRef = useRef(supervisa)
  supervisaRef.current = supervisa

  // QUIÉN TIENE «MI BANDEJA»
  //
  // El grupo Asesor —el que recibe por round-robin—, y además cualquiera que
  // tenga una conversación asignada: el supervisor o el admin que intervino o
  // que se asignó una a mano. Sin lo segundo, esa persona es dueña de una
  // conversación y no tiene dónde contestarla. `/inbox` cuenta también, para
  // que la pantalla se arme bien apenas se llega desde «Intervenir», antes
  // del primer sondeo del contador.
  const conBandeja = atiende || badges.inbox > 0 || pathname.startsWith('/inbox')
  const screen = resolveScreen(role, pathname, conBandeja)

  useEffect(() => {
    let cancelled = false
    async function poll() {
      try {
        const res = await apiClient.get<PendingCount>('/conversations/pending-count')
        if (cancelled) return
        setBadges({
          inbox: res.data.my_assigned ?? 0,
          queue: res.data.pending_unassigned ?? 0,
          documents: res.data.docs_pending_review ?? 0,
          expedientes: res.data.my_por_descargar ?? 0,
        })
        // El número de la campana. Para supervisión incluye la cola sin
        // asesor: eso es lo que ellos tienen que repartir, y es el trabajo
        // que de otro modo no aparece en ningún contador propio.
        setEsperando(supervisaRef.current
          ? (res.data.my_waiting ?? 0) + (res.data.pending_unassigned ?? 0)
          : (res.data.my_waiting ?? 0))

        // CADA ROL OYE LO QUE LE TOCA
        //
        //   asesor              -> mi cliente escribió · me derivaron una
        //                          · un expediente mío quedó listo (03-nov)
        //   supervisor y admin  -> escribió cualquiera · entró uno a la cola
        //
        // Un asesor que oyera la cola entera dejaría de distinguir lo suyo,
        // que es la forma de que una alerta deje de significar algo. Y un
        // supervisor que solo oyera lo asignado a él no se enteraría de nada:
        // su trabajo es justamente lo que todavía no tiene dueño.
        //
        // Las marcas se concatenan y se compara el conjunto: alcanza con que
        // UNA avance para que suene, y no hace falta saber cuál.
        const marca = (supervisaRef.current
          ? [res.data.any_last_inbound_ts, res.data.queue_last_ts]
          : [res.data.my_last_inbound_ts, res.data.my_last_assigned_ts,
             res.data.my_last_completo_ts]
        ).map((m) => m ?? '').join('|')

        // El primer sondeo NUNCA suena: al abrir el portal todo es «nuevo» y
        // el asesor recibiría una alerta por algo que ya sabía. Sin las
        // preferencias leídas todavía, se suena: vienen encendidas.
        const prefs = avisosRef.current
        const previa = ultimoEntrante.current
        if (previa !== null && marca !== previa && (prefs?.sonido ?? true)) {
          sonar()
        }
        ultimoEntrante.current = marca

        // QUIÉN ESCRIBIÓ: EL POP-UP DEL SISTEMA
        //
        // Sale solo si nadie está mirando el portal: con el portal al frente,
        // quien escribió sube en la lista con su número verde, y un globo de
        // Windows encima de la pantalla en la que se trabaja es ruido. Mismo
        // recorte por rol que el sonido.
        const lista = (supervisaRef.current ? res.data.entrantes : res.data.mis_entrantes) ?? []
        const nuevos = entrantesNuevos(entrantesPrevios.current, lista)
        entrantesPrevios.current = lista
        if (nuevos.length && prefs?.popup && !portalALaVista()) {
          const { titulo, opciones } = armarAviso(nuevos, {
            icono: iconoDelAviso(),
            portalALaVista: false,
            miEmail: miEmailRef.current,
            supervisa: supervisaRef.current,
          })
          // `alAbrir` es solo del camino sin service worker: con él, el clic
          // llega por el mensaje que escucha el efecto de abajo.
          avisar(titulo, opciones, () => navigateRef.current(opciones.data.url))
        }

        // UN EXPEDIENTE MÍO QUEDÓ LISTO (03-nov-2026)
        //
        // Lo pidió Leslie: el asesor se entera cuando el agente completa uno de
        // sus expedientes. Suena con la marca de arriba; acá se refresca «Mis
        // expedientes» y, con el portal en otra ventana, sale el pop-up. El
        // servidor manda la marca vacía si la regla dice que no le toca al
        // asesor, así que esto no tiene que conocer la regla.
        const listo = res.data.my_last_completo_ts ?? ''
        if (ultimoListo.current !== null && listo > ultimoListo.current) {
          window.dispatchEvent(new CustomEvent(EXPEDIENTE_LISTO))
          if (prefs?.popup && !portalALaVista()) {
            const { titulo, opciones } = avisoDeExpedienteListo(iconoDelAviso())
            avisar(titulo, opciones, () => navigateRef.current(opciones.data.url))
          }
        }
        ultimoListo.current = listo

        // LA LISTA SE ENTERA CON EL SONIDO, NO DESPUÉS
        //
        // Quien escribió sube con su número verde en la bandeja y en En vivo
        // en este mismo momento. Para el asesor cuenta cualquier estado: la
        // conversación que conduce el bot no suena, pero su número sí aparece.
        const deLaLista = (supervisaRef.current
          ? res.data.any_last_inbound_ts
          : res.data.my_any_inbound_ts) ?? ''
        if (ultimoDeLaLista.current !== null && deLaLista !== ultimoDeLaLista.current) {
          window.dispatchEvent(new CustomEvent(MENSAJE_NUEVO))
        }
        ultimoDeLaLista.current = deLaLista

        // LA BANDEJA SE ENTERA CON EL SONIDO, NO DESPUÉS
        //
        // El número baja cuando te la sacan y la marca avanza cuando te dan
        // una, así que los dos juntos cubren asignar, reasignar e intervenir.
        // Va aparte de la marca del sonido a propósito: sacarte una
        // conversación no suena, pero la bandeja igual tiene que soltarla.
        const bandeja = `${res.data.my_assigned ?? 0}|${res.data.my_last_assigned_ts ?? ''}`
        if (ultimaBandeja.current !== null && bandeja !== ultimaBandeja.current) {
          window.dispatchEvent(new CustomEvent(BANDEJA_CAMBIO))
        }
        ultimaBandeja.current = bandeja
      } catch {
        // Silencio: una caída puntual del contador no debe ensuciar la UI con
        // un error. El badge simplemente no se actualiza.
      }
    }
    // Al volver a la pestaña se sondea en el acto: el que vuelve porque sonó
    // no tiene que esperar al próximo turno del reloj.
    const alVolver = () => { if (document.visibilityState === 'visible') void poll() }
    document.addEventListener('visibilitychange', alVolver)

    void poll()
    // 5 s y no 10: es lo que tarda, como mucho, en aparecer una conversación
    // recién asignada, porque la bandeja se refresca con este sondeo. Son dos
    // consultas de una página —conversaciones y documentos—, y el hilo abierto
    // ya sondea cada 2,5 s.
    //
    // Y con un reloj de Worker (`relojDeFondo`), no `setInterval`: con la
    // pestaña de fondo Chrome lleva los timers de la página a UNO POR MINUTO
    // después de cinco minutos, y este sondeo es el que hace sonar el aviso.
    // El asesor casi nunca tiene el portal adelante: el sonido llegaba hasta
    // un minuto después de que el cliente escribiera.
    const parar = relojDeFondo(5_000, () => { void poll() })
    return () => {
      cancelled = true
      parar()
      document.removeEventListener('visibilitychange', alVolver)
    }
  }, [])

  // EL POP-UP LLEVA A LA CONVERSACIÓN
  //
  // El clic lo recibe el service worker (`public/sw.js`): trae esta pestaña al
  // frente y le pasa la ruta. `queAbrir` valida que sea una ruta del portal.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return
    const alMensaje = (e: MessageEvent) => {
      const url = queAbrir(e.data)
      if (url) navigateRef.current(url)
    }
    navigator.serviceWorker.addEventListener('message', alMensaje)
    return () => navigator.serviceWorker.removeEventListener('message', alMensaje)
  }, [])

  // Lo que la invitación de la lista y el parlante del chat necesitan.
  const { estado: estadoAvisos, activarTodo, setPanelAbierto, cambiarSonido } = avisos
  const ctxAvisos = useMemo(
    () => ({ estado: estadoAvisos, activarTodo, setPanelAbierto, cambiarSonido }),
    [estadoAvisos, activarTodo, setPanelAbierto, cambiarSonido],
  )

  // EL TÍTULO DE LA PESTAÑA TAMBIÉN AVISA
  //
  // El asesor casi nunca tiene el portal adelante: lo tiene en una pestaña
  // detrás de WhatsApp Web y del correo. Un badge que solo existe dentro de la
  // página no lo ve nadie hasta que vuelve.
  useEffect(() => {
    const base = 'Portal PGH'
    document.title = esperando > 0 ? `(${esperando}) ${base}` : base
    return () => { document.title = base }
  }, [esperando])

  // `h-full` y no `h-screen`: el alto lo pone `#root` (index.css) y el Layout
  // lo hereda, así que no hay dos reglas que se puedan desalinear (DEF-031).
  return (
    <div className="flex h-full overflow-hidden bg-surface font-sans">
      <div className="flex shrink-0">
        <Sidebar
          collapsed={sidebarCollapsed}
          onToggle={alternarSidebar}
          badges={badges}
          conBandeja={conBandeja}
        />
      </div>

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        {/* `relative z-30`: el panel de «Avisos» se despliega sobre el
            contenido, y sin un contexto propio lo tapaba el cajón del caso. */}
        <header className="relative z-30 flex h-[70px] shrink-0 items-center justify-between border-b border-surface-border bg-white px-7">
          {/* `min-w-0` y `truncate`: con la ventana angosta (DEF-031) la
              bajada larga no puede empujar la campana ni pasarse de los 70 px. */}
          <div className="min-w-0">
            <h1 className="truncate text-page font-extrabold text-ink">{screen.title}</h1>
            {screen.sub && <p className="mt-px truncate text-body text-ink-muted">{screen.sub}</p>}
          </div>

          <div className="flex items-center gap-4">
            {/* El pill lo ve quien está en el grupo Asesor, aunque su rol sea
                admin: si el round-robin le puede asignar casos, tiene que poder
                ponerse en pausa. */}
            {atiende && <AvailabilityPill />}

            {/* Buscador global: decorativo por ahora, la búsqueda cross-módulo
                no está implementada en el backend. Se deja fuera hasta que lo
                esté — un input que no busca nada es peor que ninguno. */}

            {/* «Avisos»: la campana con el NÚMERO de clientes esperando —uno o
                seis cambia si se interrumpe lo que se está haciendo— y el panel
                con el sonido, el pop-up y sus pruebas. */}
            <BotonAvisos control={avisos} esperando={esperando} />
          </div>
        </header>

        <SubTabs tabs={screen.tabs} badges={badges} />

        <main
          className={
            screen.fullBleed
              ? 'flex min-h-0 flex-1 flex-col overflow-hidden'
              : 'scrollbar-thin flex-1 overflow-y-auto px-7 py-6'
          }
        >
          {/* La bandeja y En vivo deciden columna o cajón con el ancho que el
              sidebar ocupa de verdad (DEF-052, `useDisposicion`). */}
          <SidebarColapsado.Provider value={sidebarCollapsed}>
            <AvisosCtx.Provider value={ctxAvisos}>
              <Outlet />
            </AvisosCtx.Provider>
          </SidebarColapsado.Provider>
        </main>
      </div>
    </div>
  )
}
